import {
  addSearchFilter,
  addSelector,
  addTableCampaignSelector,
  addTableProjectSelector,
  addTableUserSelector,
  attachTableSelectHandlers,
  selectedItems,
} from "@sourcewhale/shared/src/assets/js/src/general/elements/tables.js";
import { hasLicense } from "@sourcewhale/shared/src/assets/js/src/general/licenses.js";
import {
  addSpinner,
  backdropAlert,
  caseInsensitiveSort,
  getUser,
  isValidEmail,
  localDateString,
  localDateTimeString,
  nameAndPhoto,
  photoAndTooltip,
  removeSpinner,
  showSlowProviderResponseMessage,
  showToast,
  urlParams,
  uuidV4,
  wrapTooltip,
  wrappedGet,
  wrappedPost,
  yesNoConfirm,
  yesNoConfirmExtended,
} from "@sourcewhale/shared/src/assets/js/src/general/utils";
import { addWebsocketListener } from "@sourcewhale/shared/src/assets/js/src/general/websocket.js";
import { bindAiSearchButton } from "../../../svelte/src/bindings/ai-search-button.js";
import { showSearchSignpostBanner } from "../../../svelte/src/elements/Banners/SearchSignpost/stores.js";
import { showEditContact } from "../../../svelte/src/modals/TodosModal/stores.js";
import {
  companyLink,
  displayActivity,
  downloadCandidates,
  handleLeftClick,
  locationLink,
  socialLinksToIcons,
  stageTitle,
  trackingCounter,
} from "./contact-panel.js";
import { TAGS, refreshTags, registerTagEventHandlers } from "./shared/tags.js";

const campaignId = urlParams.get("campaignId");

const selectionTracking = {
  selectedIds: {},
  deselectedIds: {},
  allSelected: false,
  anchorIndex: null,
};

const showDownloadButtons =
  !getUser().adminOnlyContactExport || !!getUser().isAdmin;

const hasDataLicense = hasLicense(getUser(), "data");

const disableProjects = getUser().disableProjects || false;

export const FUNNEL_STAGE_MAP = {
  sent: [
    "sent",
    "opened",
    "clicked",
    "replied",
    "interested",
    "notInterested",
    "booked",
  ],
  opened: [
    "opened",
    "clicked",
    "replied",
    "interested",
    "notInterested",
    "booked",
  ],
  replied: ["replied", "interested", "notInterested", "booked"],
  interested: ["interested", "booked"],
  booked: ["booked"],
};

const setSelected = (candidateId, selected) => {
  if (selected) {
    delete selectionTracking.deselectedIds[candidateId];
    selectionTracking.selectedIds[candidateId] = true;
  } else {
    delete selectionTracking.selectedIds[candidateId];
    if (selectionTracking.allSelected) {
      selectionTracking.deselectedIds[candidateId] = true;
    }
  }
};

const getSelectedCount = (limit = false) => {
  const table = $("#candidatesTable").DataTable();

  if (!table.ajax.url()) return table.rows({ selected: true }).count();

  const deselectedCount = Object.keys(selectionTracking.deselectedIds).length;

  if (selectionTracking.allSelected) {
    const totalCount = table.ajax.json()
      ? table.ajax.json().recordsTotal
      : table.rows().count();

    const selectedCount = Math.max(0, totalCount - deselectedCount);
    return limit ? Math.min(selectedCount, 1000) : selectedCount;
  }

  return Object.keys(selectionTracking.selectedIds).length;
};

const updateButtonStates = () => {
  const hasSelection = selectionTracking.allSelected || getSelectedCount() > 0;
  const hasFilters = $("#resetFiltersBtn").is(":visible");

  $(".actions-button, .change-stage-button").prop("disabled", !hasSelection);
  $(".download-selected-button").toggleClass("disabled", !hasSelection);
  $(".export-all-button").toggle(!hasFilters);
  $(".download-filtered-button").toggle(hasFilters);

  $(".dt-select-all-checkbox").prop("checked", selectionTracking.allSelected);
};

const applyToCandidateIds = (callback, candidateIdsOnly) => {
  const table = $("#candidatesTable").DataTable();

  if (!table.ajax.url()) {
    const candidates = selectedItems("candidatesTable");
    if (candidateIdsOnly) callback(candidates.map((x) => x.candidateId));
    else callback(candidates);
    return;
  }

  if (candidateIdsOnly && !selectionTracking.allSelected) {
    callback(Object.keys(selectionTracking.selectedIds));
    return;
  }

  wrappedPost({
    url: "/api/candidates/query",
    contentType: "application/json",
    type: "POST",
    data: JSON.stringify({
      ...JSON.parse(table.ajax.params()),
      candidateIdsOnly,
      start: 0,
      length: 2000, // try to ensure length is big enough to cover first 1,000 selected contacts
      allSelected: true,
    }),
    success: (res) => {
      if (candidateIdsOnly)
        callback(
          res.candidateIds.filter((id) => !selectionTracking.deselectedIds[id]),
        );
      else if (selectionTracking.allSelected)
        callback(
          res.data.filter(
            (x) => !selectionTracking.deselectedIds[x.candidateId],
          ),
        );
      else
        callback(
          res.data.filter((x) => selectionTracking.selectedIds[x.candidateId]),
        );
    },
    error: () => {
      showToast({
        title: "Error",
        subtitle: "Failed to retrieve selected contacts.",
        type: "danger",
      });
      callback([]);
    },
  });
};

const performActionWithSelectedCandidates = (
  action,
  candidateIdsOnly = true,
) => {
  const count = getSelectedCount();
  const callback = (items) => action(items.slice(0, 1000));

  if (!count) {
    backdropAlert("No contacts selected");
  } else if (count >= 1000) {
    yesNoConfirm(
      "You can't perform actions on more than 1,000 contacts. Do you want to proceed with the first 1,000?",
      () => applyToCandidateIds(callback, candidateIdsOnly),
      () => {}, // Do nothing if user chooses not to proceed
    );
  } else {
    applyToCandidateIds(callback, candidateIdsOnly);
  }
};

const saveStatus = (status) => {
  performActionWithSelectedCandidates((candidateIds) => {
    const msgData = {
      type: "modify",
      targetType: "contact",
      action: { stopped: "Stop", deleted: "Delete" }[status],
      selectedCount: getSelectedCount(true),
    };

    yesNoConfirmExtended({
      msgData,
      yesCallback: () =>
        wrappedPost({
          url: "/api/candidates/modify",
          data: JSON.stringify({ candidateIds, status }),
          success: () => {
            const table = $("#candidatesTable").DataTable();
            if (status === "stopped") {
              table.rows().every((index) => {
                const row = table.row(index);
                const data = row.data();
                if (candidateIds.includes(data.candidateId)) {
                  data.status = "inactive";
                  row.invalidate();
                }
                return true;
              });
            }
          },
          spinner: ".actions-button",
        }),
    });
  });
};

const saveStage = (stage, candidateId = null) => {
  const action = (candidateIds) => {
    const msgData = {
      type: "stage",
      action: stage.charAt(0).toUpperCase() + stage.slice(1),
      selectedCount: candidateId ? 1 : getSelectedCount(true),
    };

    yesNoConfirmExtended({
      msgData,
      yesCallback: () =>
        wrappedPost({
          url: "/api/candidates/modify",
          data: JSON.stringify({ candidateIds, stage, campaignId }),
          success: () => {
            const table = $("#candidatesTable").DataTable();
            table.rows().every((index) => {
              const row = table.row(index);
              const data = row.data();
              if (candidateIds.includes(data.candidateId)) {
                data.stage = stage;
                row.invalidate();
              }
              return true;
            });
          },
          spinner: ".change-stage-button",
        }),
    });
  };
  if (candidateId) action([candidateId]);
  else performActionWithSelectedCandidates(action);
};

const addStageSelector = () => {
  const mapping = {
    sourced: '<i class="fa-regular fa-search mr-2"></i>&nbsp;Sourced',
    sent: '<i class="fa-regular fa-share mr-2"></i>&nbsp;Messaged',
    opened: '<i class="fa-regular fa-envelope-open mr-2"></i>&nbsp;Opened',
    clicked: '<i class="fa-regular fa-hand-pointer mr-2"></i>&nbsp;Clicked',
    replied: '<i class="fa-regular fa-reply mr-2"></i>&nbsp;Replied',
    interested: '<i class="fa-regular fa-grin-stars mr-2"></i>&nbsp;Interested',
    notInterested:
      '<i class="fa-regular fa-face-expressionless mr-2"></i>&nbsp;Not Interested',
    booked: '<i class="fa-regular fa-calendar mr-2"></i>&nbsp;Meeting',
  };

  const opts = Object.entries(mapping).map(([value, content]) => ({
    value,
    "data-content": content,
  }));

  addSelector("stage-selector", "stageSelector", opts, "candidatesTable", {
    multi: true,
    actionsBox: true,
    countSelectedText: "{0} stages selected",
    noneSelectedText: "Stages",
  });

  if (urlParams.get("funnel_stage")) {
    const stages = FUNNEL_STAGE_MAP[urlParams.get("funnel_stage")] || [];
    $("#stageSelector").selectpicker("val", stages);
    $("#candidatesTable").DataTable().draw();
  }
};

const addActivitySelector = () => {
  const mapping = {
    Sourced: "fas fa-search",
    "Sourced (Extension)": "fab fa-chrome",
    "Spreadsheet Upload": "fas fa-upload",
    "Added Manually": "fas fa-user-plus",
    "Imported from CRM": "fas fa-database",
    "Added to Campaign": "fas fa-paper-plane",
    "Campaign Paused": "fas fa-pause",
    "Removed from Campaign": "fas fa-bullhorn",
    "Draft Created": "fas fa-pen",
    "Draft Deleted": "fas fa-trash-alt",
    "Emailed Manually": "fas fa-user-edit",
    "Email Scheduled": "fas fa-clock",
    "Initial Email": "fas fa-share",
    "Follow-up Email": "fas fa-share",
    "Inmail Scheduled": "fab fa-linkedin",
    "Inmail Sent": "fab fa-linkedin",
    "Inmail Reply": "fas fa-reply",
    "SMS Scheduled": "fas fa-comment-dots",
    "SMS Sent": "fas fa-sms",
    "Connection Request Scheduled": "fab fa-linkedin-in",
    "Connection Request Sent": "fab fa-linkedin-in",
    "Phone Call Scheduled": "fas fa-phone",
    Phoned: "fas fa-phone-volume",
    "General Task Scheduled": "fas fa-clipboard",
    "General Task Done": "fas fa-clipboard-check",
    "Forwarded On": "fas fa-hand-point-right",
    "Meeting Booked": "fas fa-calendar-alt",
    Reply: "fas fa-reply",
    "Positive Reply": "fas fa-smile",
    "Negative Reply": "fas fa-frown",
    "Neutral Reply": "fas fa-meh",
    "Unclear Reply": "fas fa-question",
    "Autoreply Received": "fas fa-robot",
    "Outreach Stopped": "fas fa-stop",
    Unsubscribed: "fas fa-ban",
    "Email Bounced": "fas fa-hand-paper",
  };

  const opts = Object.entries(mapping).map(([value, icon]) => ({
    value,
    "data-content": `<i class="${icon} fa-fw mr-2"></i>&nbsp;${value}`,
  }));

  addSelector(
    "activity-selector",
    "activitySelector",
    opts,
    "candidatesTable",
    {
      multi: true,
      actionsBox: true,
      countSelectedText: "{0} activities selected",
      noneSelectedText: "Activities",
    },
  );
};

const addContactSelector = () => {
  const mapping = {
    Active: "fas fa-person-running",
    Finished: "far fa-check-circle",
    Removed: "fas fa-times",
  };

  const opts = Object.entries(mapping).map(([value, icon]) => ({
    value,
    "data-content": `<i class="${icon} fa-fw mr-2"></i>&nbsp;${value}`,
  }));

  addSelector("contact-selector", "contactSelector", opts, "candidatesTable", {
    multi: true,
    countSelectedText: "{0} statuses selected",
    noneSelectedText: "Statuses",
  });
};

const ACTIVITIES_DICT = {};

const fillActivities = (activityDict) => {
  const table = $("#candidatesTable").DataTable();

  const rowIds = [];
  Object.entries(activityDict).forEach(([candidateId, activities]) => {
    ACTIVITIES_DICT[candidateId] = activities;
    rowIds.push(`#contacts-table-${candidateId}`);
  });

  table.rows(rowIds).invalidate("data");
  table.columns.adjust();
};

const applyRowSelection = (row) => {
  const rowData = row.data();
  if (!rowData) return; // row is not loaded in DOM yet (likely due to ajax multi-select), skip
  const $row = $(row.node());
  const isRowSelected = selectionTracking.allSelected
    ? !selectionTracking.deselectedIds[rowData.candidateId]
    : !!selectionTracking.selectedIds[rowData.candidateId];
  $row.toggleClass("selected", isRowSelected);
};

const getMissingActivities = () => {
  const candidateIds = $("#candidatesTable")
    .DataTable()
    .rows({ page: "current" })
    .data()
    .toArray()
    .filter((row) => !ACTIVITIES_DICT[row.candidateId])
    .map((row) => row.candidateId);
  if (candidateIds.length) {
    $.post({
      url: "/api/candidates/get_activities",
      data: JSON.stringify({ candidateIds }),
      success: (res) => {
        fillActivities(res.activities);
      },
    });
  }
};

const tableButtons = [
  {
    className: "column-visibility-button",
    text: '<i class="fa-regular fa-eye"></i>',
  },
  {
    className: "actions-button",
    text: '<i class="fa-regular fa-wrench mr-2"></i>Actions',
  },
  {
    className: "change-stage-button",
    text: '<i class="fa-regular fa-flag mr-2"></i>Change Stage',
    align: "button-right",
  },
  {
    className: "add-users-button",
    text: '<i class="fa-regular fa-user-plus"></i>',
    align: "button-right",
  },
];

const columnDetails = [
  {
    title: "Links",
    name: "socialLinks:name",
  },
  {
    title: "Phones",
    name: "phones:name",
  },
  {
    title: "Job Title",
    name: "role:name",
  },
  {
    title: "Company",
    name: "company:name",
  },
  {
    title: "Location",
    name: "location:name",
  },
  {
    title: "Campaign",
    name: "campaign:name",
  },
  {
    title: "Stage",
    name: "stage:name",
  },
  {
    title: "Activities",
    name: "activities:name",
  },
  {
    title: "Opens",
    name: "opens:name",
  },
  {
    title: "Clicks",
    name: "clicks:name",
  },
  {
    title: "Tags",
    name: "tags:name",
  },
  {
    title: "Comments",
    name: "comments:name",
  },
  {
    title: "Sourcer",
    name: "sourcer:name",
  },
  {
    title: "Last Contacted",
    name: "lastContacted:name",
  },
  {
    title: "Sourced",
    name: "createdAt:name",
  },
];

const containsInvalidEmails = (candidate) => {
  const invalid = (candidate.emails || []).some((x) => !isValidEmail(x));
  if (invalid) backdropAlert("Please ensure emails are valid");
  return invalid;
};

const shortTextRender = (text, type) => {
  text = text || "";
  if (type === "display") {
    const LIMIT = 50;
    return text.length < LIMIT
      ? text
      : wrapTooltip(`<span>${text.slice(0, LIMIT)}...</span>`, text);
  }
  return text;
};

export const createCandidateTable = (options) => {
  const tableOptions = {
    buttons: {
      dom: {
        button: {
          tag: "button",
          className: "btn btn-light dropdown-toggle ml-2",
        },
      },
      buttons: tableButtons,
    },
    columns: [
      {
        data: (x) => x,
        className: "select-checkbox",
        orderable: false,
        title: '<input type="checkbox" class="dt-select-all-checkbox">',
        render: (candidate) => {
          const hiddenRow = candidate.deleted ? "hidden-row" : "";
          return `<span class="d-none candidate-id-span ${hiddenRow}" data-candidateId="${candidate.candidateId}"></span>`;
        },
      },
      {
        data: (x) => x,
        name: "contactStatus",
        render: (candidate) => {
          const activeStatus = candidate.isOld ? "old" : candidate.status;
          if (activeStatus === "active")
            return wrapTooltip(
              '<div class="user-status bg-success"><span style="display:none">Active Contacts</span></div>',
              "Active",
            );
          return activeStatus === "old"
            ? wrapTooltip(
                '<i class="fas fa-circle-exclamation"><span style="display:none">Removed Contacts</span></i>',
                "Contact is no longer<br>on this campaign",
              )
            : '<div style="display:none">Finished Contacts</div>';
        },
      },
      {
        data: (x) => x.enrichments || [],
        render: (enrichments, type) => {
          const now = Math.round(Date.now() / 1000);
          return type === "display" && enrichments.some((x) => now < x)
            ? wrapTooltip(
                '<i class="fas fa-circle-notch fast-spin"></i>',
                "Contact is enriching",
              )
            : "";
        },
      },
      {
        data: (x) => x,
        name: "name",
        title: "Name",
        orderable: true,
        render: (candidate, type) => {
          if (type === "display") {
            const namePhoto = nameAndPhoto(
              candidate.name,
              candidate.photo,
              "hover-highlight",
            );
            const email =
              candidate.emails && candidate.emails.length
                ? candidate.emails[0]
                : "";
            const hasPhone =
              candidate.phones && candidate.phones.length
                ? '<i class="fa-solid fa-phone-alt text-primary mr-2" style="font-size: 0.7rem"></i>'
                : "";
            return `<div class='user-col d-inline-flex flex-column'>
                    ${namePhoto}
                    <p class="my-0 d-inline-flex align-items-center" style='padding-top:0.2rem'>
                      ${hasPhone}<span class='hover-highlight candidate-profile-link'>${email}</span>
                    </p>
                  </div>`;
          }
          return candidate.name || "";
        },
      },
      {
        data: (x) => x.socialLinks || [],
        visible: false,
        name: "socialLinks",
        title: "Links",
        render: (socialLinks, type) =>
          type === "display"
            ? socialLinksToIcons(socialLinks)
            : socialLinks.join(", "),
      },
      {
        data: (x) => x.phones || [],
        name: "phones",
        title: "Phones",
        visible: false,
        render: (phones, type) => {
          if (type === "display") {
            return phones
              .map((x) => `<span class="badge badge-primary">${x}</span>`)
              .join("<br>");
          }
          return phones.join("");
        },
      },
      {
        data: (x) => x.role || "",
        name: "role",
        title: "Job Title",
        visible: false,
        orderable: true,
        render: shortTextRender,
      },
      {
        data: (x) => x.company || "",
        name: "company",
        title: "Company",
        orderable: true,
        render: (company, type) =>
          type === "display" ? companyLink(company) : company,
      },
      {
        data: (x) => x.location || "",
        name: "location",
        title: "Location",
        visible: false,
        orderable: true,
        render: (location, type) =>
          type === "display" ? locationLink(location) : location,
      },
      {
        data: (x) => x,
        name: "campaign",
        title: "Campaign",
        visible: !options.hideCampaigns,
        orderable: !options.ajax,
        render: (candidate, type) => {
          if (type === "display") {
            return candidate.campaign
              ? `<a href="/campaign?campaignId=${candidate.campaignId}" target="_blank">${candidate.campaign}</a>`
              : "";
          }
          return type === "filter"
            ? candidate.campaignId || "none"
            : candidate.campaign || "";
        },
      },
      {
        data: "stage",
        name: "stage",
        title: "Stage",
        orderable: true,
        render: (stage, type, row) => {
          const SORT_MAP = {
            sourced: 1,
            sent: 2,
            opened: 3,
            clicked: 4,
            replied: 5,
            notInterested: 6,
            interested: 7,
            booked: 8,
          };
          return type === "sort"
            ? SORT_MAP[stage] || 7
            : stageTitle(stage, {
                html: type === "display",
                candidateId: row.candidateId,
              });
        },
      },
      {
        data: "candidateId",
        name: "activities",
        title: "Activities",
        render: (candidateId, type) => {
          const activities = ACTIVITIES_DICT[candidateId] || [];
          const maxDisplayedActivities = 10;
          if (type === "display") {
            const displayedActivities = activities.slice(
              0,
              maxDisplayedActivities,
            );
            let content = displayedActivities
              .map((activity) => displayActivity(activity, true))
              .join("");

            if (activities.length > maxDisplayedActivities) {
              const moreActivities = activities.slice(maxDisplayedActivities);
              const tooltipContent = moreActivities
                .map((activity) => {
                  const timeInfo =
                    activity.scheduled && !activity.manual
                      ? ` (${localDateTimeString(activity.timestamp)})`
                      : "";
                  return `${activity.name}${timeInfo}`;
                })
                .join("<br>");

              const moreActivitiesIcon = wrapTooltip(
                `<i class="fas fa-ellipsis-h more-activities-icon"></i>`,
                tooltipContent,
              );

              content += moreActivitiesIcon;
            }

            return content;
          }
          return activities.map(displayActivity).join("");
        },
      },
      {
        data: (x) => x,
        name: "opens",
        title: "Opens",
        orderable: true,
        className: "text-center",
        render: (candidate, type) => {
          if (candidate.opens === null) return "";
          if (type === "display") {
            return trackingCounter(
              candidate.opens,
              "open",
              candidate.candidateId,
              candidate.name,
            );
          }
          return candidate.opens;
        },
      },
      {
        data: (x) => x,
        name: "clicks",
        title: "Clicks",
        orderable: true,
        className: "text-center",
        render: (candidate, type) => {
          if (candidate.clicks === null) return "";
          if (type === "display") {
            return trackingCounter(
              candidate.clicks,
              "click",
              candidate.candidateId,
              candidate.name,
            );
          }
          return candidate.clicks;
        },
      },
      {
        data: (x) => x.tagIds || [],
        name: "tags",
        title: "Tags",
        className: "text-center tags-column",
        visible: false,
        render: (tagIds, type) => {
          const tags = (tagIds || [])
            .map((tagId) => TAGS.find((x) => x.tagId === tagId))
            .filter(Boolean)
            .map((tag) => tag.title);

          tags.sort();

          if (type === "display") {
            const divs = tags.map(
              (tag) =>
                `<div class='ml-1 px-2 badge badge-primary d-inline-flex tag-title'>${tag}</div>`,
            );
            return divs.join("");
          }
          return tags.join(", ");
        },
      },
      {
        data: (x) => x.comments || "",
        name: "comments",
        title: "Comments",
        className: "text-center",
        visible: false,
        orderable: !options.disableCommentsOrdering && !options.ajax,
        render: (comments) =>
          comments.length
            ? wrapTooltip(
                `<i class="candidate-comment-preview fas fa-comment p-1" style="cursor:pointer"></i>`,
                `<p class='comment-preview text-left p-1 m-0'>${comments}</p>`,
              )
            : null,
      },
      {
        data: "sourcer",
        name: "sourcer",
        title: "Sourcer",
        className: "text-center",
        render: (sourcer, type) =>
          type === "display" ? photoAndTooltip(sourcer) : sourcer.name || "",
      },
      {
        data: "createdAt",
        name: "createdAt",
        title: "Sourced",
        orderable: true,
        className: "text-center",
        visible: false,
        render: (createdAt, type) =>
          ["display", "filter"].includes(type)
            ? localDateString(createdAt, false)
            : createdAt,
      },
      {
        data: (x) => x.lastContacted || "",
        name: "lastContacted",
        title: "Last Contacted",
        visible: false,
        orderable: true,
        className: "text-center",
        render: (lastContacted, type) => {
          if (type === "display" || type === "filter") {
            return lastContacted ? localDateString(lastContacted, false) : null;
          }
          return lastContacted;
        },
      },
      { data: (x) => x.emails || [], visible: false },
    ],
    columnDefs: [{ targets: "_all", orderable: false }],
    fnInfoCallback: (_, start, end, __, total) => {
      const baseInfo = `<b>${start}-${end}</b>&nbsp;of&nbsp;${total}`;

      const selectedCount = getSelectedCount();
      if (selectedCount > 0) {
        const count = selectedCount === total ? "all" : selectedCount;
        return `${baseInfo}&nbsp;&nbsp;&nbsp;(${count} selected)`;
      }

      return baseInfo;
    },
    deferRender: true,
    createdRow(row, _, dataIndex) {
      if (options.ajax) {
        applyRowSelection(this.api().row(row));

        // Get the starting index of the current page
        const pageInfo = this.api().page.info();
        const startIndex = pageInfo.start;

        // Calculate the global index of the row
        const rowIndex = startIndex + dataIndex;

        // Store the global index as a data attribute on the row element
        $(row).attr("data-row-index", rowIndex);
      }
    },
    drawCallback: (settings) => {
      if (options.ajax) {
        const api = new $.fn.dataTable.Api(settings);
        api.rows({ page: "current" }).every((index) => {
          applyRowSelection(api.row(index));
          return true;
        });
        updateButtonStates();
        const json = candidatesTable.ajax.json();
        showSearchSignpostBanner.set(json && json.recordsFiltered <= 3);
      }
      getMissingActivities();
      $("#candidatesTable").DataTable().scroller.measure(false);
    },
    dom: `
        <'table-nav-wrapper row mb-3'
          <'col table-nav d-flex align-items-center'
            <'search-filter-button btn btn-light mr-2'>
            fB
            ${options.aiSearchContainer && getUser().hasCrm ? '<"ai-search-button-container">' : ""}
          >
        >
        <'row'
          <'col-12't>
          <'col-12'r>
        >
        <'row mx-1 my-4 d-flex align-items-center'
          <'col-4'l>
          <'col-4 text-center'i>
          <'col-4'p>
        >`,
    language: {
      select: {
        rows: {
          _: "", // Suppress default selection message for multiple rows
          0: "", // No message when no rows are selected
          1: "", // No message when one row is selected
        },
      },
      info: "",
      infoFiltered: "",
      search:
        '<i class="fa-regular fa-magnifying-glass" aria-hidden="true"></i>',
      searchPlaceholder: "Search by name, company, email or phone",
      emptyTable: "No matching contacts",
      infoEmpty: "No matching contacts",
      zeroRecords: "No matching contacts",
      processing:
        '<i class="spinner-grow text-primary" style="position: fixed; top: 50vh; left: 50vw"></i>',
    },
    scroller: {
      displayBuffer: 30,
      boundaryScale: 0,
    },
    scrollX: true,
    scrollY: "80vh",
    colReorder: { fixedColumnsLeft: 1 },
    pagingType: "numbers",
    rowId: (x) => `contacts-table-${x.candidateId}`,
    stateSave: true,
    stateDuration: 60 * 60 * 24 * 3,
    stateLoadParams: (_, data) => {
      data.scroller = {
        baseRowTop: 0,
        baseScrollTop: 0,
        scrollTop: 0,
        topRow: 0,
      };
      data.search.search = "";
      data.start = 0;
      data.columns.forEach((col) => {
        col.search.search = "";
      });
    },
  };

  if (options.ajax) {
    tableOptions.ajax = options.ajax;
    tableOptions.processing = true;
    tableOptions.serverSide = true;
  } else {
    tableOptions.select = { style: "multi+shift", selector: "td:first-child" };
  }

  const candidatesTable = $("#candidatesTable").DataTable(tableOptions);

  if (options.aiSearchContainer && getUser().hasCrm) {
    bindAiSearchButton();
  }

  const attachAjaxSelectboxHandlers = () => {
    $("#candidatesTable_wrapper").on(
      "click",
      "thead .dt-select-all-checkbox",
      (e) => {
        selectionTracking.allSelected = $(e.target).prop("checked");
        selectionTracking.selectedIds = {};
        selectionTracking.deselectedIds = {};

        candidatesTable.rows({ page: "current" }).every((index) => {
          applyRowSelection(candidatesTable.row(index));
          return true;
        });

        updateButtonStates();
        candidatesTable.draw(false);
      },
    );

    const getRowIndex = ($row) => parseInt($row.attr("data-row-index"), 10);

    const handleAjaxMultiSelect = (candidateId) => {
      const $targetRow = $(`#contacts-table-${candidateId}`);
      const targetRowIndex = getRowIndex($targetRow);

      const $anchorRow = $(
        `tr[data-row-index="${selectionTracking.anchorIndex}"]`,
      );
      const anchorRowIndex = selectionTracking.anchorIndex;

      // Determine range of rows to process
      const startIndex = Math.min(anchorRowIndex, targetRowIndex);
      const endIndex = Math.max(anchorRowIndex, targetRowIndex);

      const allRowsLoaded = $anchorRow.length && $targetRow.length;

      const processRows = (candidateIds, targetId) => {
        // If target row is selected (or not explicitly deselected in "Select All" mode) -> deselect range
        // If target row is not selected (or explicitly deselected in "Select All" mode) -> select range
        const targetIsSelected = selectionTracking.allSelected
          ? !selectionTracking.deselectedIds[targetId]
          : !!selectionTracking.selectedIds[targetId];
        const shouldSelect = !targetIsSelected;

        candidateIds.forEach((id) => {
          setSelected(id, shouldSelect);
          const $row = candidatesTable.row(`#contacts-table-${id}`);
          if ($row.node()) applyRowSelection($row);
        });

        updateButtonStates();
      };

      // Handle rows that might not be loaded due to infinite scroll
      if (allRowsLoaded) {
        const candidateIds = candidatesTable
          .rows((_, __, node) => {
            const $thisRow = $(node);
            const rowIndex = getRowIndex($thisRow);
            return rowIndex >= startIndex && rowIndex <= endIndex;
          })
          .data()
          .toArray()
          .map((rowData) => rowData.candidateId);

        processRows(candidateIds, candidateId);
      } else {
        // Fetch non-visible rows from server
        const ajaxParams = {
          ...JSON.parse(candidatesTable.ajax.params()),
          start: startIndex,
          length: endIndex - startIndex + 1,
          candidateIdsOnly: true,
        };

        wrappedPost({
          url: "/api/candidates/query",
          contentType: "application/json",
          data: JSON.stringify(ajaxParams),
          success: (res) => {
            processRows(res.candidateIds, candidateId);
          },
          error: () => {
            showToast({
              title: "Error",
              subtitle:
                "An unexpected error occurred while selecting contacts. Please try again or contact customer support.",
              type: "error",
            });
          },
        });
      }
    };

    const handleRegularSelect = ($row, candidateId) => {
      const isRowSelected = !$row.hasClass("selected");
      const rowIndex = getRowIndex($row);
      setSelected(candidateId, isRowSelected);
      $row.toggleClass("selected", isRowSelected);
      selectionTracking.anchorIndex = rowIndex;
    };

    $("#candidatesTable").on("click", "tbody tr", (e) => {
      const target = $(e.target);
      if (
        target.hasClass("select-checkbox") ||
        target.closest(".select-checkbox").length
      ) {
        const $row = target.closest("tr");

        const candidateId = $row
          .find(".candidate-id-span")
          .attr("data-candidateId");

        const trackSelectionWithShiftKey = !!(
          e.shiftKey &&
          selectionTracking.anchorIndex >= 0 &&
          options.ajax
        );

        if (trackSelectionWithShiftKey) handleAjaxMultiSelect(candidateId);
        else handleRegularSelect($row, candidateId);

        updateButtonStates();
      }
    });
  };

  const toggleTapHighlightOnShift = () => {
    $(document).on("keydown keyup", (event) => {
      if (event.key === "Shift") {
        $("body").css(
          "user-select",
          event.type === "keydown" ? "none" : "auto",
        );
      }
    });
  };

  if (options.ajax) {
    attachAjaxSelectboxHandlers();
    toggleTapHighlightOnShift();
  } else {
    attachTableSelectHandlers("candidatesTable", false, () => {
      const disabled = !getSelectedCount();
      $(".actions-button, .change-stage-button").prop("disabled", disabled);
      $(".download-selected-button").toggleClass("disabled", disabled);
    });
  }

  if (options.topContainer) {
    $(".table-nav-wrapper").removeClass("mb-3").appendTo("#top-container");
  }

  if (showDownloadButtons) {
    $(".download-opts-button")
      .prop({
        id: "downloadOptsButton",
        "data-toggle": "dropdown",
        "aria-haspopup": "true",
        "aria-expanded": "false",
      })
      .wrap('<div class="dropdown" id="downloadButtonDropdownWrapper"></div>');

    const downloadHtml = `
      <div class="dropdown-menu dropdown-menu-right" style="width: fit-content;">
        <a class="dropdown-item download-selected-button disabled"><i class="fa-regular fa-arrow-alt-circle-down fa-fw"></i>&ensp;Download Selected</a>
        <a class="dropdown-item download-all-button"><i class="fa-regular fa-arrow-alt-circle-down fa-fw"></i>&ensp;Download All</a>
        <a style="display: none;" class="dropdown-item download-filtered-button"><i class="fa-regular fa-arrow-alt-circle-down fa-fw"></i>&ensp;Download All Filtered</a>
        <a style="display: none;" class="dropdown-item export-all-button"><i class="fa-regular fa-arrow-alt-circle-down fa-fw"></i>&ensp;Download All</a>
      </div>`;

    $(downloadHtml).insertAfter(".download-opts-button");

    $(".download-opts-button").dropdown("update");
  }

  $(".column-visibility-button")
    .prop({
      id: "columnVisibilityButton",
      "data-toggle": "dropdown",
      "aria-haspopup": "true",
      "aria-expanded": "false",
    })
    .wrap(
      '<div class="dropdown" id="columnVisibilityButtonDropdownWrapper"></div>',
    );

  const getColumnVisibilityItems = () =>
    columnDetails
      .map((value) => {
        const id = `columnVisibility-${value.name.split(":")[0]}`;
        const isVisible = candidatesTable.column(value.name).visible();
        const className = isVisible ? "dropdown-item active" : "dropdown-item";
        return `<a class="${className}" id="${id}">${value.title}</a>`;
      })
      .join("");

  const columnVisibilityHtml = /* html */ `
    <div class="dropdown-menu" aria-labelledby="columnVisibilityButton" id="columnVisibilityButtonDropdown" style="width:fit-content">
      ${getColumnVisibilityItems()}
    </div>`;
  $(columnVisibilityHtml).insertAfter(".column-visibility-button");
  $(".column-visibility-button").dropdown("update");

  columnDetails.forEach((value) => {
    const id = `columnVisibility-${value.name.split(":")[0]}`;
    $(`#${id}`).on("click", () => {
      const column = candidatesTable.column(value.name);
      const isVisible = column.visible();
      if (isVisible) {
        $(`#${id}`).removeClass("active");
      } else {
        $(`#${id}`).addClass("active");
      }
      column.visible(!isVisible);
    });
  });

  $("#columnVisibilityButtonDropdown").on("click", (e) => {
    $(e.currentTarget).addClass("show");
    $("#columnVisibilityButtonDropdownWrapper").addClass("show");
    e.stopPropagation();
  });

  $(".change-stage-button, .actions-button").prop("disabled", true);

  $(".change-stage-button")
    .prop({
      id: "changeStageButton",
      "data-toggle": "dropdown",
      "aria-haspopup": "true",
      "aria-expanded": "false",
    })
    .wrap('<div class="dropdown" id="changeStageButtonDropdownWrapper"></div>');

  const changeStateHtml = /* html */ `
    <div class="dropdown-menu" aria-labelledby="changeStageButton" id="changeStageButtonDropdown" style="width:fit-content">
      <a class="dropdown-item" id="sourcedDropdownSelected"><i class="fa-regular fa-search fa-fw mr-2"></i> Sourced</a>
      <a class="dropdown-item" id="sentDropdownSelected"><i class="fa-regular fa-share fa-fw mr-2"></i> Messaged</a>
      <a class="dropdown-item" id="openedDropdownSelected"><i class="fa-regular fa-envelope-open fa-fw mr-2"></i> Opened</a>
      <a class="dropdown-item" id="repliedDropdownSelected"><i class="fa-regular fa-reply fa-fw mr-2"></i> Replied</a>
      <a class="dropdown-item" id="interestedDropdownSelected"><i class="fa-regular fa-grin-stars fa-fw mr-2"></i> Interested</a>
      <a class="dropdown-item" id="notInterestedDropdownSelected"><i class="fa-regular fa-face-expressionless fa-fw mr-2"></i> Not Interested</a>
      <a class="dropdown-item" id="bookedDropdownSelected"><i class="fa-regular fa-calendar fa-fw mr-2"></i> Meeting</a>
    </div>`;
  $(changeStateHtml).insertAfter(".change-stage-button");
  $(".change-stage-button").dropdown("update");

  $(".actions-button")
    .prop({
      id: "actionsButton",
      "data-toggle": "dropdown",
      "aria-haspopup": "true",
      "aria-expanded": "false",
    })
    .wrap('<div class="dropdown" id="actionsButtonDropdownWrapper"></div>');

  const withDataLicenseRequiredTooltip = (html) =>
    hasDataLicense
      ? html
      : wrapTooltip(html, "Enriching via SourceWhale requires a data license");

  const dataLicenseDisabled = hasDataLicense ? "" : "disabled";
  const enrichBtns = [
    withDataLicenseRequiredTooltip(`<div class="dropdown-submenu ${dataLicenseDisabled}"><div id="enrichDropdown" class="dropdown-item dropdown-toggle"><i class="fa-regular fa-envelope fa-fw mr-2"></i>&thinsp;Enrich Emails...</div>
        <div id="enrichDropdownSubmenu" class="dropdown-menu">
            <div class="dropdown-item enrich-btn ${dataLicenseDisabled}" data-kind="personal">
              <i class="fa-regular fa-user fa-fw mr-2"></i>&thinsp;Personal Emails
            </div>
            <div class="dropdown-item enrich-btn ${dataLicenseDisabled}" data-kind="work">
              <i class="fa-regular fa-briefcase fa-fw mr-2"></i>&thinsp;Work Emails
            </div>
            <div class="dropdown-item enrich-btn ${dataLicenseDisabled}" data-kind="both">
              <i class="fa-regular fa-link fa-fw mr-2"></i>&thinsp;Both
            </div>
        </div>
      </div>`),
  ];

  const user = getUser();
  Object.entries(user.searchKinds || {}).forEach(([kind, val]) => {
    if (kind === "phone") {
      enrichBtns.push(
        withDataLicenseRequiredTooltip(`
          <a class="dropdown-item enrich-btn ${dataLicenseDisabled}" data-kind=${kind}>
            <i class="fa-regular fa-phone fa-fw mr-2"></i>&thinsp;Enrich ${val.title}s
          </a>`),
      );
    } else if (kind !== "email") {
      enrichBtns.push(`
          <a class="dropdown-item enrich-btn" data-kind=${kind} data-provider-title="${
            val.title
          }">
            <img class="mr-2" src="/assets/img/licenses/${
              kind.split("--")[0]
            }.png" style="width: 1rem; height: 1rem; margin-bottom: 0.15rem" />&thinsp;Enrich with ${
              val.title
            }
          </a>
      `);
    }
  });

  const conditionalDropdown = (visible, kind) => {
    if (!visible) return "";
    const disabled = user.isOnAnotherTeam ? "disabled" : "";
    const HTML_MAP = {
      stopOutreach: {
        html: `<div class="dropdown-divider"></div><a class="${disabled} dropdown-item stop-button"><i class="fa-regular fa-user-times fa-fw mr-2"></i>&thinsp;Stop Outreach</a>`,
        tooltip: "stop outreach",
      },
      deleteContact: {
        html: `<a class="${disabled} dropdown-item delete-button"><i class="fa-regular fa-user-minus fa-fw mr-2"></i>&thinsp;Delete Contact</a>`,
        tooltip: "delete a contact",
      },
      addProject: {
        html: `<a class="${disabled || disableProjects ? "disabled" : ""} dropdown-item add-to-project-button"><i class="fa-regular fa-address-book fa-fw mr-2"></i>&thinsp;Add to Project</a>`,
        tooltip: "add contacts to projects",
      },
      addCompany: {
        html: `<a class="${disabled} dropdown-item add-to-company-button"><i class="fa-regular fa-building fa-fw mr-2"></i>&thinsp;Add to Company</a>`,
        tooltip: "add contacts to companies",
      },
      removeProject: {
        html: `<a class="${disabled} dropdown-item remove-from-project-button"><i class="fa-regular fa-address-book fa-fw mr-2"></i>&thinsp;Remove from Project</a>`,
        tooltip: "remove contacts from projects",
      },
      addCampaign: {
        html: `<a class="${disabled} dropdown-item add-to-campaign-button"><i class="fa-regular fa-bullhorn fa-fw mr-2"></i>&thinsp;Add to Campaign</a>`,
        tooltip: "add to campaign",
      },
      removeCampaign: {
        html: `<a class="${disabled} dropdown-item remove-from-campaign-button"><i class="fa-solid fa-bullhorn fa-fw mr-2"></i>&thinsp;Remove from Campaign</a>`,
        tooltip: "remove contacts from campaigns",
      },
      addUnsubscribes: {
        html: `<a class="${disabled} dropdown-item add-to-unsubscribes"><i class="fa-regular fa-ban fa-fw mr-2"></i>&thinsp;Add to Unsubscribes</a>`,
        tooltip: "add a contact to the unsubscribes list",
      },
    };

    if (kind === "addProject" && disableProjects)
      return wrapTooltip(
        HTML_MAP[kind].html,
        "Purchase Projects to enable this feature",
      );

    if (disabled === "disabled") {
      return wrapTooltip(
        HTML_MAP[kind].html,
        `You cannot ${HTML_MAP[kind].tooltip} when viewing another team`,
      );
    }
    return HTML_MAP[kind].html;
  };

  const actionsHtml = /* html */ `
      <div id="actionsMenu" class="dropdown-menu" style="width: fit-content;">
        <div id="push-to-crm-button-div"></div>
        <div class="dropdown-divider"></div>
        ${conditionalDropdown(true, "addProject")}
        ${conditionalDropdown(user.hasCrm, "addCompany")}
        <a class="dropdown-item assign-tags-button">
          <i class="fa-regular fa-tags fa-fw mr-2"></i>&thinsp;Tags
        </a>
        ${conditionalDropdown(options.removeFromProjectBtn, "removeProject")}
        ${conditionalDropdown(!user.isExtensionOnly, "addCampaign")}
        ${conditionalDropdown(options.removeFromCampaignBtn, "removeCampaign")}
        ${conditionalDropdown(true, "addUnsubscribes")}
        <div class="dropdown-divider"></div>
        ${enrichBtns.join("")}
        ${conditionalDropdown(!user.isExtensionOnly, "stopOutreach")}
        ${conditionalDropdown(options.deleteContactBtn, "deleteContact")}
      </div>`;
  $(actionsHtml).insertAfter(".actions-button");
  $(".actions-button").dropdown("update");

  $(`#enrichDropdown`).on("click", (e) => {
    e.stopPropagation();
    $(`#enrichDropdownSubmenu`).toggle();
  });

  // Hide submenu on menu close
  $(document).click(() => {
    if ($("#actionsMenu:visible").length === 1)
      $(`#enrichDropdownSubmenu`).hide();
  });

  $(".enrich-btn").on("click", (e) => {
    const action = $(e.currentTarget).attr("data-kind");
    performActionWithSelectedCandidates((candidateIds) => {
      const msgData = {
        type: "enrich",
        action,
        providerTitle: $(e.currentTarget).attr("data-provider-title"),
        selectedCount: candidateIds.length,
      };

      yesNoConfirmExtended({
        msgData,
        yesCallback: () => {
          const data = {
            candidateIds,
            contactTypes:
              msgData.action === "both"
                ? ["work", "personal"]
                : [msgData.action],
            current: true,
          };
          wrappedPost({
            url: "/api/candidates/start_searches",
            data: JSON.stringify(data),
            success: () => {
              showToast({
                title: "Enrichment started",
                type: "success",
              });
            },
          });
        },
      });
    });
  });

  $(".add-users-button")
    .prop({
      id: "addUsersButton",
      "data-toggle": "dropdown",
      "aria-haspopup": "true",
      "aria-expanded": "false",
    })
    .wrap('<div class="dropdown" id="addUsersButtonDropdownWrapper"></div>');

  const addUsersHtml = `
      <div class="dropdown-menu dropdown-menu-right" style="width: fit-content;">
        <a class="dropdown-item" id="manualAddButton">
          <i class="fa-regular fa-square-plus fa-fw mr-2"></i>&thinsp;Add Manually
        </a>
        <a class="dropdown-item" id="manualAddButton" href="/upload">
          <i class="fa-regular fa-cloud-upload-alt fa-fw mr-2"></i>&thinsp;Spreadsheet Upload
        </a>
      </div>`;
  $(addUsersHtml).insertAfter(".add-users-button");
  $(".add-users-button").dropdown("update");

  addSearchFilter();

  $(".add-to-project-button").click(() =>
    $("#addToProjectModal").modal("show"),
  );
  $(".add-to-company-button").click(() => {
    // Clear previous selection and input when modal opens
    const $selector = $("#addCompanySelector");
    if ($selector.length && $selector.data("ui-autocomplete")) {
      $selector.val("").attr("data-company-id", "");
      $selector.autocomplete("close");
    }
    $("#addToCompaniesModal").modal("show");
  });
  $(".add-to-campaign-button").click(() =>
    $("#addToCampaignModal").modal("show"),
  );
  $(".stop-button").click(() => saveStatus("stopped"));
  $(".delete-button").click(() => saveStatus("deleted"));

  $(".download-selected-button").click(() => {
    performActionWithSelectedCandidates(downloadCandidates, false);
  });

  $(".download-filtered-button").click(() => {
    const table = $("#candidatesTable").DataTable();

    wrappedPost({
      url: "/api/candidates/query",
      contentType: "application/json",
      type: "POST",
      data: JSON.stringify({
        ...JSON.parse(table.ajax.params()),
        length: table.ajax.json().recordsFiltered,
      }),
      success: (res) => downloadCandidates(res.data),
      spinner: "#downloadOptsButton",
    });
  });

  $(".download-all-button").click(() => {
    const allCandidates = $("#candidatesTable")
      .DataTable()
      .rows()
      .data()
      .toArray();
    downloadCandidates(allCandidates);
  });

  $(".export-all-button").click(() => {
    const exportContacts = () => {
      wrappedPost({
        url: "/api/candidates/export",
        success: () => {
          showToast({
            title: "Contacts exported successfully",
            subtitle:
              "You will be emailed a CSV with all contacts in a few minutes",
            type: "success",
          });
        },
        error: () =>
          showToast({
            title: "Unable to download contacts",
            subtitle:
              "Please try again or contact support if the issue persists",
            type: "danger",
          }),
      });
    };

    if (candidatesTable.ajax.json().recordsTotal > 12000) {
      yesNoConfirm(
        `You can only export up to 12,000 contacts at a time. </br></br>Alternatively, use the filters and the "Download All Filtered" button to export smaller, more specific groups.`,
        () => exportContacts(),
        () => {},
        `Export first 12,000`,
        "Cancel",
        () => {}, // Do nothing if user chooses not to proceed
      );
    } else exportContacts();
  });

  addStageSelector(candidatesTable);
  addActivitySelector(candidatesTable);
  addContactSelector(candidatesTable);

  if (!options.ajax) {
    $("#stageSelector").on("change", () => {
      const val = $("#stageSelector").selectpicker("val");
      const regex = `^(${val.map(stageTitle).join("|")})`;
      candidatesTable.columns("stage:name").search(regex, true, false).draw();
    });

    $("#activitySelector").on("change", () => {
      const val = $("#activitySelector").selectpicker("val");
      candidatesTable
        .columns("activities:name")
        .search(val.join("|"), true, false)
        .draw();
    });

    $("#contactSelector").on("change", () => {
      const val = $("#contactSelector").selectpicker("val");
      candidatesTable
        .columns("contactStatus:name")
        .search(val.join("|"), true, false)
        .draw();
    });
  }

  const campaignSelectorCallback = (campaigns) => {
    const addCampaignSelector = $("#addCampaignSelector");
    const manualAddSelector = $("#manualAddCampaignSelector");

    const yourCampaignsOptGroup = $("<optgroup>", {
      label: "Your Campaigns",
    });
    const teammatesCampaignsOptGroup = $("<optgroup>", {
      label: "Teammates Campaigns",
    });

    if (campaigns) {
      caseInsensitiveSort(Object.keys(campaigns)).forEach((i) => {
        const campaign = campaigns[i];
        const opts = {
          value: campaign.campaignId,
          "data-content": `<i class="fa-regular fa-bullhorn mr-2"></i>${campaign.campaignName}`,
        };

        if (campaign.creatorUserId === getUser().userId) {
          yourCampaignsOptGroup.append($("<option>", opts));
        } else {
          teammatesCampaignsOptGroup.append($("<option>", opts));
        }
      });
    }

    addCampaignSelector
      .append(yourCampaignsOptGroup)
      .append(teammatesCampaignsOptGroup);
    manualAddSelector
      .append(yourCampaignsOptGroup.clone())
      .append(teammatesCampaignsOptGroup.clone());

    manualAddSelector.prepend(
      $("<option>", {
        value: "none",
        "data-content": "Nothing selected",
      }),
    );

    addCampaignSelector.selectpicker("refresh");
    manualAddSelector
      .selectpicker("val", campaignId || "none")
      .selectpicker("refresh");
  };

  const campaignSelectorOpts = {
    multi: true,
    includeNone: true,
    callback: campaignSelectorCallback,
    noneSelectedText: "Campaigns",
    actionsBox: true,
  };

  addTableCampaignSelector({
    tableId: "candidatesTable",
    options: campaignSelectorOpts,
    campaignData: options.campaignData,
  });

  const projectSelectorOpts = {
    multi: true,
    includeNone: true,
    noneSelectedText: "Projects",
    selectAllBtn: true,
    actionsBox: true,
    currentUserOnly: getUser().kind !== "superUser",
    openOnly: true,
    disabled: disableProjects,
  };

  addTableProjectSelector("candidatesTable", projectSelectorOpts);

  if (options.ajax) {
    addTableUserSelector("candidatesTable", "sourcer", "Sourcer", {
      multi: true,
      includeAll: false,
      selectSelf: false,
      noneSelectedText: "Sourcers",
      countSelectedText: "{0} sourcers selected",
      actionsBox: true,
      useURLParams: true,
    });
  } else {
    campaignSelectorOpts.onChange = () => {
      const val = $("#campaignSelector").selectpicker("val");
      candidatesTable
        .columns("campaign:name")
        .search(val.join("|"), true, false)
        .draw();
    };
  }

  $(document).on("change", "select#filterByStatus", () => {
    candidatesTable.draw();
  });

  if (disableProjects) {
    $("#addToProjectButton").prop("disabled", true);
    $("#addProjectSelector").prop("disabled", true).selectpicker("refresh");
    $("#manualAddProjectSelector")
      .prop("disabled", true)
      .selectpicker("refresh");
  } else {
    wrappedGet({
      url: "/api/projects/get_all",
      success: (projects) => {
        const selector = $("#addProjectSelector");
        const manualSelector = $("#manualAddProjectSelector");

        [selector, manualSelector].forEach((s) => {
          s.append(
            projects.map((p) =>
              $("<option>", { value: p.projectId, text: p.projectName }),
            ),
          );
          s.selectpicker("refresh");
        });

        manualSelector
          .selectpicker("val", urlParams.get("projectId") || "none")
          .selectpicker("refresh");
      },
    });
  }

  $("#candidatesTable tbody").on("click", ".candidate-profile-link", (e) =>
    handleLeftClick(e, "", "candidatesTable"),
  );

  $(document).on("click", () => {
    $(".dt-buttons .dropdown-menu, .dt-buttons .dropdown").removeClass("show");
  });

  $(document).on("show.bs.dropdown", () => {
    $(".dt-buttons .dropdown-menu, .dt-buttons .dropdown").removeClass("show");
  });

  $("#addToCampaignButton").click(() => {
    const action = (candidates) => {
      const mappingData = {
        candidates,
        searchEmails: "none",
        searchPhones: "none",
        bulkVerify: false,
        contactTypes: [],
        campaignId: $("#addCampaignSelector").selectpicker("val"),
      };

      wrappedPost(
        {
          url: "/api/spreadsheet/map",
          data: JSON.stringify(mappingData),
          success: (res) => {
            const params = {
              campaignId: mappingData.campaignId,
              existingContacts: true,
              spreadsheetMappingResultId: res.spreadsheetMappingResultId,
            };
            window.location = `/review?${new URLSearchParams(params).toString()}`;
          },
          spinner: "#addToCampaignButton",
        },
        false,
      );
    };
    performActionWithSelectedCandidates(action, false);
  });

  $("#manualAddemails").tokenfield({
    createTokensOnBlur: true,
    inputType: "email",
  });
  $("#manualAddphones").tokenfield({
    createTokensOnBlur: true,
    inputType: "tel",
  });
  $("#manualAddsocialLinks")
    .tokenfield({
      createTokensOnBlur: true,
      inputType: "url",
    })
    .on("change", () => {
      const tokenField = $("#manualAddsocialLinks-tokenfield");
      tokenField.attr(
        "placeholder",
        tokenField.siblings(".token").length > 0
          ? null
          : "e.g. linkedin.com/in/williamhgates",
      );
    });

  $(document).on("click", "#manualAddButton", () =>
    $("#manualAddModal").modal("show"),
  );

  $("#manualAddAddButton").click(() => {
    const FIELDS = ["name", "company", "role", "school", "location"];

    const candidate = FIELDS.reduce((ret, field) => {
      ret[field] = $(`#manualAdd${field}`).val();
      return ret;
    }, {});

    ["emails", "phones", "socialLinks"].forEach((key) => {
      candidate[key] = $(`#manualAdd${key}`)
        .tokenfield("getTokens")
        .map((x) => x.value);
    });

    candidate.source = "addedManually";

    if (containsInvalidEmails(candidate)) return;

    const data = {
      campaignId: $("#manualAddCampaignSelector").selectpicker("val"),
      candidates: [candidate],
      addedManually: true,
      projectIds: $("#manualAddProjectSelector").selectpicker("val"),
      tagIds: $("#manualCandidateTagSelector").selectpicker("val"),
    };

    if (["none", null].includes(data.campaignId)) {
      wrappedPost({
        url: "/api/outreach/add",
        data: JSON.stringify(data),
        success: () => {
          $("#manualAddModal").modal("hide");

          FIELDS.forEach((field) => {
            $(`#manualAdd${field}`).val("");
          });
          ["emails", "phones", "socialLinks"].forEach((field) => {
            $(`#manualAdd${field}`).tokenfield("setTokens", []);
          });
        },
        spinner: "#manualAddAddButton",
      });
    } else {
      wrappedPost({
        url: "/api/outreach/generate",
        data: JSON.stringify(data),
        success: (res) => {
          const params = {
            outreachId: res.outreachId,
            campaignId: data.campaignId,
            addedManually: 1,
          };
          window.location = `/outreach?${new URLSearchParams(
            params,
          ).toString()}`;
        },
        spinner: "#manualAddAddButton",
      });
    }
  });

  // TODO: Make comment box focussed on this event
  $("#candidatesTable tbody").on("click", ".candidate-comment-preview", (e) => {
    handleLeftClick(e, "", "candidatesTable");
    showEditContact.set(true); // Open new edit contact page
  });

  registerTagEventHandlers({
    applyToIds: (callback) => performActionWithSelectedCandidates(callback),
  });

  $("#addToProjectButton").click(() => {
    performActionWithSelectedCandidates((candidateIds) => {
      const projectId = $("#addProjectSelector").selectpicker("val");
      const data = { add: {} };
      data.add[projectId] = candidateIds;

      wrappedPost({
        url: "/api/projects/associate",
        data: JSON.stringify(data),
        success: () => {
          showToast({
            title: "Contacts added to project",
            type: "success",
          });
          $("#addToProjectModal").modal("hide");
        },
        spinner: "#addToProjectButton",
      });
    });
  });

  $("#addToCompanyButton").click(() => {
    performActionWithSelectedCandidates((candidateIds) => {
      const companyId = $("#addCompanySelector").attr("data-company-id");

      if (!companyId) {
        backdropAlert("Please select a company");
        return;
      }

      wrappedPost({
        url: "/api/candidates/modify",
        data: JSON.stringify({
          candidateIds,
          companyId,
        }),
        success: () => {
          showToast({
            title: "Contacts added to company",
            type: "success",
          });
          $("#addToCompaniesModal").modal("hide");
        },
        spinner: "#addToCompanyButton",
      });
    });
  });

  $(document).on("click", ".add-to-unsubscribes", () => {
    performActionWithSelectedCandidates((candidateIds) => {
      const msgData = {
        type: "modify",
        targetType: "contact",
        action: "Unsubscribe",
        selectedCount: candidateIds.length,
      };

      yesNoConfirmExtended({
        msgData,
        yesCallback: () => {
          const table = $("#candidatesTable").DataTable();
          const selectedCandidates = table
            .rows()
            .data()
            .toArray()
            .filter((candidate) =>
              candidateIds.includes(candidate.candidateId),
            );

          const selectedCandidateEmails = selectedCandidates.map(
            (candidate) => candidate.emails || [],
          );

          wrappedPost({
            url: "/api/unsubscribes/add",
            data: JSON.stringify({
              data: selectedCandidateEmails.flat().toString(),
            }),
            success: () => {
              const title = "Contacts added to unsubscribed list";
              const subtitle = selectedCandidateEmails.some((x) => !x.length)
                ? "Selected contacts with no emails associated to them have not been affected"
                : "";
              showToast({ title, subtitle, type: "success" });
            },
            spinner: ".actions-button",
          });
        },
      });
    });
  });

  const setSyncAsVisibilities = (provider, syncAs, val) => {
    $(`#${provider}ModalAdBtn`).toggle(syncAs !== "");

    $(
      `#${provider}ModalDiv select, #${provider}ModalDiv .freetext-override`,
    ).each((_, el) => {
      const kind = $(el).attr("data-kind");
      if (kind === "syncAs") return;

      const providerOverrides = val.overrides || {};
      const providerFreetextOverrides = val.freetextOverrides || {};

      const id = $(el).attr("id");
      const optionValue =
        providerOverrides[kind] || providerFreetextOverrides[kind];

      if (optionValue.syncAsTitles) {
        $(`#${id}-label`).text(
          optionValue.syncAsTitles[syncAs] || optionValue.title,
        );
      }
      if (optionValue.syncAsRestrictions) {
        $(`#${id}-row`).toggle(optionValue.syncAsRestrictions.includes(syncAs));
      }
    });
  };

  const addCrmSelectOption = (
    provider,
    optionValue,
    kind,
    opts,
    adBtn = false,
  ) => {
    const { title } = optionValue;
    const label = kind === "syncAs" ? "Sync as" : title;
    const isAdditional = !(kind === "syncAs" || optionValue.mandatory);
    const placeholder = isAdditional ? "(Optional)" : "Choose...";

    const id =
      kind === "syncAs"
        ? `${provider}ModalAdvancedOptionsSyncAsSelector`
        : uuidV4();

    const liveSearch =
      Object.keys(opts).length > 10 ? 'data-live-search="true"' : "";

    const icon = adBtn
      ? `<i class="ml-2 fas fa-arrow-circle-down" style="cursor: pointer;" id="${provider}ModalAdBtn"></i>`
      : "";
    const multi =
      kind === "syncAs" || optionValue.singleChoice ? "" : "multiple";

    const actionsBox =
      multi && Object.keys(opts).length > 10 ? "true" : "false";

    const html = `
        <div class="row my-2" id="${id}-row">
          <div class="col d-flex align-items-center">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text" style="white-space: break-spaces" id="${id}-label">${label}</span>
              </div>
              <select
                id="${id}"
                data-provider="${provider}"
                data-text-title="${title}"
                data-kind="${kind}"
                class="selectpicker form-control"
                title="${placeholder}"
                ${multi}
                ${liveSearch}
                data-actions-box="${actionsBox}"
                data-size="10"
              >
              </select>
            </div>
            ${icon}
          </div>
        </div>`;

    if (kind === "syncAs") $(`#${provider}ModalDiv`).append(html);
    else if (optionValue.mandatory)
      $(`#${provider}ModalMandatoryDiv`).append(html);
    else $(`#${provider}ModalAdDiv`).append(html);

    $(`#${provider}ModalAdBtn`)
      .tooltip("dispose")
      .tooltip({ title: "More Options" });

    $(`#${id}`).append(
      Object.entries(opts).map(([text, value]) =>
        $("<option>", { value, text }),
      ),
    );

    $(`#${id}`).selectpicker("refresh");
  };

  const addCrmInputOption = (provider, value, kind) => {
    const id = uuidV4();

    const placeholder = value.mandatory ? "" : "(Optional)";

    const html = `
        <div class="row my-2" id="${id}-row">
          <div class="col d-flex align-items-center">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text" id="${id}-label">${value.title}</span>
              </div>
              <textarea
                id="${id}"
                class="form-control freetext-override"
                data-provider="${provider}"
                data-text-title="${value.title}"
                data-kind="${kind}"
                placeholder=${placeholder}
              ></textarea>
            </div>
          </div>
        </div>`;

    if (value.mandatory) $(`#${provider}ModalMandatoryDiv`).append(html);
    else $(`#${provider}ModalAdDiv`).append(html);
  };

  wrappedGet({
    url: "/api/integrations/get_all",
    success: (res) => {
      Object.entries(res).forEach(([provider, val]) => {
        if (!val.syncKinds) return;

        $("#push-to-crm-button-div").append(`
          <a class="dropdown-item" id="${provider}-push-to-crm-button">
            <img class="mr-2" src="/assets/img/crm/${provider}.png" style="width: 1rem; height: 1rem; margin-bottom: 0.15rem" />&thinsp;Push to ${val.title}
          </a>
        `);

        $(`#${provider}-push-to-crm-button`).click(() => {
          performActionWithSelectedCandidates((candidateIds) => {
            $("#pushToCrmModalTitle").text(`Push to ${val.title}`);

            $("#pushToCrmModal .modal-body").html(
              `<div class="push-to-crm-options-div" id="${provider}ModalDiv"></div>`,
            );

            const { syncAsLabelMap } = val;

            const syncKinds = val.syncKinds.reduce((ret, syncKind) => {
              ret[syncAsLabelMap[syncKind] || syncKind] = syncKind;
              return ret;
            }, {});

            addCrmSelectOption(
              provider,
              val,
              "syncAs",
              syncKinds,
              !!val.overrides,
            );

            if (val.overrides || val.freetextOverrides) {
              $(`#${provider}ModalDiv`).append(
                `<div id="${provider}ModalMandatoryDiv"></div>`,
              );
              $(`#${provider}ModalDiv`).append(
                `<div id="${provider}ModalAdDiv"></div>`,
              );

              $(`#${provider}ModalAdDiv`).hide();

              const setAdDivVisibility = () => {
                const hidden = $(`#${provider}ModalAdDiv`).is(":hidden");
                $(`#${provider}ModalAdBtn`)
                  .removeClass(`fa-arrow-circle-${hidden ? "up" : "down"}`)
                  .addClass(`fa-arrow-circle-${hidden ? "down" : "up"}`);
              };

              const mandatoryOverrides = Object.values(
                val.overrides || {},
              ).some((v) => !!v.mandatory);

              const mandatoryFreetextOverrides = Object.values(
                val.freetextOverrides || {},
              ).some((v) => !!v.mandatory);

              const anyMandatory =
                mandatoryOverrides || mandatoryFreetextOverrides;

              $(`#${provider}ModalAdDiv`).toggle(!anyMandatory);

              setAdDivVisibility();

              $(`#${provider}ModalAdBtn`).click(() => {
                $(`#${provider}ModalAdDiv`).toggle();
                setAdDivVisibility();
              });
            }

            Object.entries(val.overrides || {}).forEach(([k, v]) => {
              addCrmSelectOption(provider, v, k, v.values);
            });

            Object.entries(val.freetextOverrides || {}).forEach(([k, v]) => {
              addCrmInputOption(provider, v, k);
            });

            setSyncAsVisibilities(provider, "", val);

            $(`#${provider}ModalAdvancedOptionsSyncAsSelector`).change(() => {
              const syncAs = $(
                `#${provider}ModalAdvancedOptionsSyncAsSelector`,
              ).selectpicker("val");
              setSyncAsVisibilities(provider, syncAs, val);
            });

            if (val.syncKinds.length === 1) {
              $(`#pushToCrmModal .selectpicker[data-kind="syncAs"]`)
                .selectpicker("val", val.syncKinds[0])
                .change();
            }

            $("#pushToCrmModal").modal("show");

            $("#pushToCrmBtn")
              .off("click")
              .click(() => {
                const atsSettings = {};
                atsSettings[provider] = { overrides: {} };

                $("#pushToCrmModal .selectpicker").each((_, el) => {
                  if (!$(el).is(":visible")) return;
                  const kind = $(el).attr("data-kind");
                  if (kind === "syncAs")
                    atsSettings[provider].syncAs = $(el).selectpicker("val");
                  else
                    atsSettings[provider].overrides[kind] =
                      $(el).selectpicker("val");
                });

                $("#pushToCrmModal textarea").each((_, el) => {
                  if (!$(el).is(":visible")) return;
                  const kind = $(el).attr("data-kind");
                  atsSettings[provider].overrides[kind] = $(el).val();
                });

                if (!atsSettings[provider].syncAs) {
                  backdropAlert("Please select a sync option");
                  return;
                }

                wrappedPost({
                  url: "/api/candidates/push_to_crm",
                  data: JSON.stringify({ candidateIds, atsSettings }),
                  success: () => {
                    $("#pushToCrmModal").modal("hide");
                    showToast({
                      title: "Contacts successfully pushed to CRM",
                      subtitle: "Results may take a few minutes to appear",
                      type: "success",
                    });
                  },
                  spinner: "#pushToCrmBtn",
                });
              });
          });
        });
      });
    },
  });

  addWebsocketListener((message) => {
    if (message.kind === "update-candidates") {
      let changed = false;
      message.changes.forEach((change) => {
        const { update } = change;
        const { ids } = change;
        const isOld = campaignId && update.campaignId !== campaignId;
        const rows = candidatesTable.rows().data().toArray();
        rows.forEach((row) => {
          if (ids.includes(row.candidateId)) {
            Object.entries(update).forEach(([key, val]) => {
              if (!(isOld && key === "stage")) row[key] = val;
            });
            changed = true;
          }
        });
      });
      if (changed) candidatesTable.rows().invalidate();
    }

    if (message.kind === "updateOldStage") {
      const { candidate } = message;
      if (campaignId !== candidate.campaignId) return;
      candidatesTable
        .rows()
        .data()
        .toArray()
        .forEach((row) => {
          if (row.isOld && row.candidateId === candidate.candidateId) {
            Object.entries(candidate).forEach(([key, val]) => {
              row[key] = val;
            });
          }
        });
      candidatesTable.rows().invalidate();
    }
  });

  const searchQuery = urlParams.get("contact");
  $('#candidatesTable_filter input[type="search"').val(searchQuery);
  if (searchQuery) $("#candidatesTable").DataTable().search(searchQuery).draw();

  $("#candidatesTable").on("click", "tbody tr td", (e) => {
    if (
      e.target !== e.currentTarget ||
      $(e.currentTarget).hasClass("select-checkbox")
    )
      return;
    $(e.currentTarget).parent().find(".select-checkbox").click();
  });
};

export const initAddToCompaniesModal = () => {
  const $addCompanySelector = $("#addCompanySelector");

  // Check if element exists before proceeding
  if (!$addCompanySelector.length) {
    return;
  }

  $('[data-toggle="tooltip"]').tooltip();

  $addCompanySelector.autocomplete({
    source: (request, response) => {
      const { term } = request;
      wrappedPost({
        url: "/api/companies/query",
        data: JSON.stringify({
          draw: 1,
          columns: [
            {
              data: "name",
              name: "",
              searchable: true,
              orderable: true,
            },
          ],
          search: { value: term, regex: false },
          start: 0,
          length: 100,
          order: [{ column: 0, dir: "desc" }],
        }),
        success: (data) => {
          response(data.data || []);
        },
      });
    },
  });

  // Wait for autocomplete to be fully initialized before setting _renderItemData
  setTimeout(() => {
    const autocompleteWidget = $addCompanySelector.data("ui-autocomplete");
    if (autocompleteWidget) {
      autocompleteWidget._renderItemData = (ul, item) => {
        const { companyId, name, website } = item;

        $(ul).append(
          `
<div id="company${companyId}" class="company-select">
  <img src="/assets/img/icons/building-solid.svg" class="mr-2" style="width: 1.5rem; height: 1.5rem">
  <div>
    <span class="company-name">${name}</span>
    ${website ? `<i class="company-website">${website}</i>` : ""}
  </div>
</div>`,
        );

        $(`#company${companyId}`).on("click", () => {
          $addCompanySelector.val(name);
          $addCompanySelector.attr("data-company-id", companyId);
          $addCompanySelector.autocomplete("close");
        });
      };
    }
  }, 0);

  $addCompanySelector.on("focus", () => {
    $addCompanySelector.autocomplete("search", $addCompanySelector.val());
  });
};

export const initializeContactUtils = () => {
  refreshTags({});

  initAddToCompaniesModal();

  [
    "sourced",
    "sent",
    "opened",
    "replied",
    "interested",
    "notInterested",
    "booked",
  ].forEach((stage) => {
    $(document).on("click", `#${stage}Dropdown`, (e) => {
      const candidateId = e.currentTarget.getAttribute("data-candidateId");
      saveStage(stage, candidateId);
    });

    $(document).on("click", `#${stage}DropdownSelected`, () =>
      saveStage(stage),
    );
  });

  addWebsocketListener((data) => {
    if (
      data.kind === "candidateCampaignActivities" &&
      window.location.href.includes(`/campaign?campaignId=${data.campaignId}`)
    )
      fillActivities(data.activityMap);
  });

  $(document).on("click", ".tracking-icon", (e) => {
    $("#candidateHistoryNameSpan").text($(e.target).attr("data-candidateName"));
    $("#candidateHistoryModalBody").empty();
    $("#candidateHistoryModal").modal("show");

    if (!parseInt($(e.target).attr("data-count"), 10)) {
      $("#candidateHistoryModalBody").append("<p>No history to show</p>");
      return;
    }
    const kind = $(e.target).attr("data-kind");
    const candidateId = $(e.target).attr("data-candidateId");
    wrappedGet({
      url: "/api/activities/get_tracking",
      data: { candidateId, kind, campaignId },
      success: (res) => {
        const title = kind === "open" ? "Subject" : "Source";
        let tableHtml = `<table style="width: 100%;"><thead><tr><th style="padding: 0.2rem;">Date</th><th style="padding: 0.2rem;">${title}</th><th style="padding: 0.2rem;">Step</th><th style="padding: 0.2rem;">Campaign</th></tr></thead><tbody>`;
        res.forEach((item) => {
          const step = item.stepNumber ? `Step ${item.stepNumber}` : "-";
          const source = item.source ? item.source : "-";
          const timestamp = localDateTimeString(item.timestamp);
          const style = `width:fit-content; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 0.2rem;`;
          tableHtml += `<tr>
              <td style="${style} font-size: small;">${timestamp}</td>
              <td style="${style} max-width:300px;">${source}</td>
              <td style="${style}">${step}</td>
              <td style="${style} max-width:150px;"><a target="_blank" href="/campaign?campaignId=${item.campaignId}">${item.campaignName}</a></td>
            </tr>`;
        });
        tableHtml += `</tbody></table>`;
        $("#candidateHistoryModalBody").html(tableHtml);
      },
    });
  });

  $(document).on("click", ".reply-icon", (e) => {
    $("#replyParagraph").text("");
    $("#replyCategorizerModal").modal("show");
    addSpinner("#replyParagraph");
    const identifier = $(e.target).attr("data-identifier");
    const identifierMap = {
      inmailHash: "inmailHash",
      "message-id": "messageId",
    };
    wrappedGet({
      url: "/api/activities/get_reply",
      data: {
        identifier,
        [identifier]: $(e.target).attr(`data-${identifierMap[identifier]}`),
      },
      success: (res) => {
        $("#replyParagraph").text(res.messageTextPlain);
        removeSpinner("#replyParagraph");
      },
      error: (err) => {
        if (err.status === 504) showSlowProviderResponseMessage();
        removeSpinner("#replyParagraph");
      },
    });

    ["interested", "notInterested", "followUpLater"].forEach((category) => {
      $(`#${category}ReplyBtn`)
        .off("click")
        .click(() => {
          const data = {
            identifier,
            [identifier]: $(e.target).attr(`data-${identifierMap[identifier]}`),
            category,
          };
          wrappedPost({
            url: "/api/activities/save_reply_category",
            data: JSON.stringify(data),
            success: (res) => {
              const table = $("#candidatesTable").DataTable();
              table
                .rows()
                .data()
                .toArray()
                .forEach((row) => {
                  (ACTIVITIES_DICT[row.candidateId] || []).forEach((a) => {
                    if (a[identifier] !== res.activity[identifier]) return;

                    Object.entries(res.activity).forEach(([k, v]) => {
                      a[k] = v;
                    });

                    row.stage = res.stage;
                  });
                });
              table.rows().invalidate();
              $("#replyCategorizerModal").modal("hide");
            },
          });
        });
    });
  });

  if (showDownloadButtons)
    tableButtons.push({
      className: "download-opts-button",
      text: '<i class="fa-regular fa-download"></i>',
      align: "button-right",
    });
};
